import { Grid2, Grid2Props, TextFieldProps, Typography } from "@mui/material";
import { FC, useState } from "react";
import { useAppForm } from "src/hooks/tanstack/form";
import { SalifyFormValidator, ValidationType } from "src/lib/utils";
import { gridWrapperConstant } from "src/lib/utils/constants";
import componentRegistry from "..";
import SalifyDefaultButton from "../button/default";
import axios from "axios";
import { toast } from "react-toastify";
import clientFetcher from "src/lib/fetcher/client";

interface SalifyFormWrapperDefaultProps {
  components?: Array<{
    blockName: string | undefined;
    variant: string | undefined;
    props?: {
      name: string | undefined;
      validation: ValidationType | undefined;
      required?: boolean;
      requiredMessage?: string;
      [key: string]: any;
    };
    colSpan: any;
  }>;
  fields?: Array<{
    blockName: string | undefined;
    variant: string | undefined;
    name: string | undefined;
    validation: ValidationType | undefined;
    colSpan: any;
    textFieldProps?:
      | ({
          requiredMessage?: string;
        } & TextFieldProps)
      | undefined;
  }>;
  columnTotal?: number;
  gridProps?: Grid2Props;
  buttonSubmitText?: string;
  buttonSubmitProps?: {
    sx?: any;
    [key: string]: any;
  };
  submitButtonAlignment?: "flex-start" | "center" | "flex-end";
}

export interface VariantProps extends SalifyFormWrapperDefaultProps {}

const SalifyFormWrapperDefault: FC<SalifyFormWrapperDefaultProps> = ({
  columnTotal,
  gridProps,
  fields,
  components,
  buttonSubmitText,
  buttonSubmitProps,
  submitButtonAlignment,
}) => {
  const [isSubmitingForm, setIsSubmitingForm] = useState(false);

  // Use components if provided, otherwise fall back to fields, with empty array as default
  const formFields = components || fields || [];

  const defaultValues = formFields.reduce(
    (acc, field) => {
      // Handle both component structure and field structure
      const fieldName = "props" in field ? field.props?.name : (field as any).name;
      if (fieldName) {
        acc[fieldName] = "";
      }
      return acc;
    },
    {} as Record<string, any>,
  );

  const form = useAppForm({
    defaultValues,
    onSubmit: async ({ value }) => {
      setIsSubmitingForm(true);
      try {
        const productNote = value.productInterest?.trim()
          ? `Sản phẩm quan tâm: ${value.productInterest.trim()}`
          : "";
        const message = [value.content, productNote].filter(Boolean).join("\n\n");

        const response = await clientFetcher.sendEmail({
          name: value.fullName,
          email: value.email,
          phone: value.phone,
          message,
        });
      } catch (error) {
      } finally {
        setIsSubmitingForm(false);
      }
    },
  });

  return (
    <form style={{ margin: "20px" }}>
      <Grid2
        container
        columns={columnTotal || gridWrapperConstant.colTotal}
        {...gridProps}
        spacing={gridProps?.spacing ?? { mobile: 1, tablet: 2, laptop: 3 }}
      >
        {!!formFields?.length ? (
          formFields.map((fieldInfo, index) => {
            const Component = fieldInfo.blockName
              ? ((componentRegistry as any)?.[fieldInfo.blockName]?.[
                  fieldInfo.variant || "default"
                ] ?? null)
              : null;

            // Handle both component structure and field structure
            const fieldName =
              "props" in fieldInfo ? fieldInfo.props?.name : (fieldInfo as any).name;
            const fieldProps =
              "props" in fieldInfo
                ? fieldInfo.props || {}
                : (fieldInfo as any).textFieldProps || {};

            return (
              <Grid2
                key={fieldName || index}
                size={{
                  mobile: fieldInfo.colSpan?.mobile || columnTotal || gridWrapperConstant.colTotal,
                  tablet: fieldInfo.colSpan?.tablet,
                  laptop: fieldInfo.colSpan?.laptop,
                  desktop: fieldInfo.colSpan?.desktop,
                }}
              >
                {Component ? (
                  fieldName ? (
                    <form.Field
                      key={fieldName}
                      name={fieldName}
                      validators={{
                        onChange: SalifyFormValidator.dynamicFormValidator({
                          required: fieldProps.required ?? false,
                          type:
                            "props" in fieldInfo
                              ? fieldProps.validation
                              : (fieldInfo as any).validation,
                          requiredMessage: fieldProps.requiredMessage,
                        }),
                      }}
                    >
                      {(tanStackField) => {
                        // Pass appropriate props based on component type
                        const componentProps =
                          fieldInfo.blockName === "select-field"
                            ? { selectFieldProps: fieldProps }
                            : { textFieldProps: fieldProps };

                        return (
                          <Component field={tanStackField} {...componentProps} {...fieldProps} />
                        );
                      }}
                    </form.Field>
                  ) : fieldInfo.variant === "hidden" ? (
                    <></>
                  ) : (
                    <Typography color="red">
                      {`Field name của trường ${fieldProps?.label} undefined`}
                    </Typography>
                  )
                ) : (
                  <Typography color="red">
                    Chưa hỗ trợ component với block name: {fieldInfo.blockName}
                  </Typography>
                )}
              </Grid2>
            );
          })
        ) : (
          <Typography color="red">{`Chưa cấu hình Field`}</Typography>
        )}
      </Grid2>

      <Grid2
        container
        justifyContent={submitButtonAlignment || "flex-start"}
        sx={{ mt: 2.5 }} // 20px margin top using Material-UI spacing (2.5 * 8px = 20px)
      >
        <SalifyDefaultButton
          text={buttonSubmitText || "Gửi yêu cầu"}
          buttonProps={{
            loading: isSubmitingForm,
            onClick: form.handleSubmit,
            ...buttonSubmitProps,
          }}
        />
      </Grid2>
    </form>
  );
};

SalifyFormWrapperDefault.displayName = "SalifyFormWrapperDefault";

export default SalifyFormWrapperDefault;
